import { useEffect, useRef, useCallback } from 'react';

import PropTypes from 'prop-types';

import Switch from '#globalComponents/Forms/Switch';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

import { getStatusBadge } from '../utils/statusBadgeCreator';

const EmailContactsList = ({
	emails,
	selectedEmailId,
	onSelectEmail,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
}) => {
	const hideApprovedEmails = usePreferencesStore((s) => s.hideApprovedEmails);
	const setHideApprovedEmails = usePreferencesStore((s) => s.setHideApprovedEmails);

	const sentinelRef = useRef(null);
	const scrollContainerRef = useRef(null);

	const handleLoadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		const container = scrollContainerRef.current;
		if (!sentinel || !container) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					handleLoadMore();
				}
			},
			{ root: container, rootMargin: '200px' },
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [handleLoadMore]);

	return (
		<div className="bg-white border rounded-card overflow-hidden flex flex-col h-[100vh]">
			<div className="p-3 border-b bg-bgBase-300 flex items-center justify-between">
				<h3 className="font-medium text-text">Empfänger</h3>
				<label className="flex items-center gap-1.5 cursor-pointer select-none">
					<span className="text-xs text-bgBase-800">Genehmigte ausblenden</span>
					<Switch size="sm" checked={hideApprovedEmails} onChange={setHideApprovedEmails} />
				</label>
			</div>
			<div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
				{emails.length === 0 && (
					<div className="p-4 text-center text-sm text-bgBase-800">Alle E-Mails wurden genehmigt.</div>
				)}
				{emails.map((email) => {
					// Company recipients have no contact person: show "Firmenkontakt" + the company
					// (from the recipientCompany join), mirroring the lead view.
					const isCompanyRecipient = !email.crmContacts;
					const recipientName = isCompanyRecipient
						? 'Firmenkontakt'
						: email.crmContacts?.fullName || 'Unbekannt';
					const companyName = isCompanyRecipient
						? email.recipientCompany?.companyName || '-'
						: email.crmContacts?.crmCompanies?.companyName || '-';

					return (
						<div
							key={email.id}
							onClick={() => onSelectEmail(email.id)}
							className={`p-4 border-b cursor-pointer transition-colors ${
								selectedEmailId === email.id
									? 'bg-primary-100 border-l-4 border-l-primary-600'
									: 'hover:bg-bgBase-300'
							}`}
						>
							<div className="flex items-start justify-between mb-1">
								<div className="flex-1 min-w-0">
									<p className="font-medium text-text truncate">{recipientName}</p>
									<p className="text-sm text-bgBase-800 truncate">{companyName}</p>
								</div>
								<div className="ml-2 flex-shrink-0">{getStatusBadge(email.status)}</div>
							</div>
							<p className="text-xs text-text truncate mt-1">{email.generatedSubject}</p>
						</div>
					);
				})}

				{/* Sentinel element for infinite scroll */}
				<div ref={sentinelRef} className="h-1" />

				{isFetchingNextPage && <div className="p-3 text-center text-sm text-bgBase-800">Laden...</div>}
			</div>
		</div>
	);
};

EmailContactsList.propTypes = {
	emails: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			status: PropTypes.string.isRequired,
			generatedSubject: PropTypes.string.isRequired,
			crmContacts: PropTypes.object,
			recipientCompany: PropTypes.object,
		}),
	).isRequired,
	selectedEmailId: PropTypes.string,
	onSelectEmail: PropTypes.func.isRequired,
	fetchNextPage: PropTypes.func.isRequired,
	hasNextPage: PropTypes.bool,
	isFetchingNextPage: PropTypes.bool,
};

export default EmailContactsList;
